import type { ReactNode } from "react";
import { Outlet } from "react-router";
import { cn } from "@/lib/utils";
import { Sidebar, SidebarRail } from "./sidebar";
import { ProjectTopbar, Topbar } from "./topbar";

/**
 * The shared frame: a fixed rail on the left, a white bar on top, and a single
 * scrolling column. Only the column scrolls — the frame is pinned to the
 * viewport, which is why the body never scrolls horizontally at any width.
 */
function Frame({
  rail,
  bar,
  children,
}: {
  rail: ReactNode;
  bar: ReactNode;
  /** Defaults to the routed `<Outlet/>`; passed explicitly by callers that
   *  render their own content, such as `ProjectLayout`'s pre-resolution states. */
  children?: ReactNode;
}) {
  return (
    <div className="flex h-screen w-full overflow-hidden bg-canvas text-ink">
      {rail}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {bar}
        {/*
          `relative` is load-bearing, not decoration. `overflow-y-auto` clips
          normal-flow children, but an absolutely positioned descendant is
          positioned against its nearest *positioned* ancestor — and with this
          column static, that is the document itself. Such a child escapes the
          clip and extends the page's scrollable area, so the whole shell
          scrolls up into blank canvas while every element still measures
          exactly one viewport tall.

          It is easy to reintroduce and hard to spot: `sr-only` is absolutely
          positioned, and so is every icon sitting in a parent that forgot
          `relative`. Step 3 had 32 of them and 1531px of empty scroll.
          Making this column the containing block fixes the class of bug rather
          than today's instances of it.
        */}
        <div className="relative min-h-0 flex-1 overflow-y-auto">
          {children ?? <Outlet />}
        </div>
      </div>
    </div>
  );
}

/**
 * The chrome for anything above a project — the picker, the create flow, and
 * the states `ProjectLayout` renders before it knows which project it has.
 *
 * Exported with a `children` escape hatch so those pre-resolution states get
 * the real rail and topbar rather than a bare column. Without it, opening any
 * project flashed an unstyled page, and "Project not found" had no sign-out and
 * no way back except its own button.
 */
export function WorkspaceChrome({ children }: { children?: ReactNode }) {
  return (
    <Frame rail={<SidebarRail />} bar={<Topbar eyebrow="Workspace" title="Projects" />}>
      {children}
    </Frame>
  );
}

/**
 * The chrome for every screen inside a project: full nav, project-aware title.
 * Rendered by `ProjectLayout`, which is what guarantees a project exists.
 */
export function AppShell() {
  return <Frame rail={<Sidebar />} bar={<ProjectTopbar />} />;
}

/**
 * The route element for screens above any project — the picker and the create
 * flow. Renders the routed outlet inside `WorkspaceChrome`.
 *
 * A separate shell rather than teaching `Sidebar`/`Topbar` a "no project" mode.
 * That mode would live inside the two components every screen depends on, and
 * it would mean the project could no longer be resolved once, at the layout, in
 * a place where a missing one can be reported properly.
 */
export function PickerShell() {
  return <WorkspaceChrome />;
}

/**
 * The standard content column: centred, 1180px wide, with the design's
 * asymmetric vertical padding — generous at the bottom so the last card never
 * sits flush against the viewport edge. Today opens tighter (16px) than the
 * other screens (28px) because its greeting row carries the space instead.
 */
export function ContentColumn({
  children,
  tight = false,
  className,
}: {
  children: ReactNode;
  tight?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mx-auto max-w-[1180px] px-[30px] pb-[60px]",
        tight ? "pt-4" : "pt-7",
        className,
      )}
    >
      {children}
    </div>
  );
}
